import { createSelector } from 'reselect';
import { TIME_SERIES_CATEGORY } from '../../../CommonCharts/Trend/constants';
import UserCapabilityHelper from 'helpers/userCapabilityHelper';

export const getSelectedAsset = ({ powertrainAssetDetails }) => {
    return powertrainAssetDetails.selectedAsset;
};

export const getSelectedAssetV2 = ({ conditionIndices }) => {
    return conditionIndices?.assets;
};

export const getIsInternalTabLimitActive = ({ operationalParameters }) => {
    return operationalParameters?.isInternalTabLimitActive;
};

export const getAssetActiveTab = ({ detailedInformation }) => {
    return detailedInformation?.activeTab;
};

const getUserCapabilities = ({ overview }) => overview?.userCapabilities;

const getIsOperationalParametersEnergyCapabilityEnabled = createSelector([
    getUserCapabilities
], (capability) => {
    const userCapabilityHelper = new UserCapabilityHelper(capability);
    return userCapabilityHelper.hasOperationalParametersEnergyCapability();
});

// Get energy-related KPIs list
const getEnergyRelatedKPIsList = createSelector([
    ({ operationalParameters }) => operationalParameters?.availableKPIs?.trend?.data,
    getIsOperationalParametersEnergyCapabilityEnabled
], (trendKPIs, isOperationalParametersEnergyCapabilityEnabled) => {
    if (!isOperationalParametersEnergyCapabilityEnabled) {
        return [];
    }

    const energyRelatedSignals = [];
    trendKPIs?.forEach((kpi) => {
        const kpiObj = kpi.data;
        const keys = Object.keys(kpiObj);
        keys?.forEach(key => {
            const newObj = kpiObj[key];
            // Filter for energy-related signals based on timeseriesCategory field from API
            const isEnergyByCategory = newObj?.timeseriesCategory === TIME_SERIES_CATEGORY.ENERGY;
            if (isEnergyByCategory) {
                energyRelatedSignals.push(newObj);
            }
        });
    });
    return energyRelatedSignals;
});

// Get operational parameters KPIs list
const getOperationalParametersKPIsList = createSelector([
    ({ operationalParameters }) => operationalParameters?.availableKPIs?.trend?.data,
    getIsOperationalParametersEnergyCapabilityEnabled
], (trendKPIs, isOperationalParametersEnergyCapabilityEnabled) => {
    const operationalSignals = [];
    trendKPIs?.forEach((kpi) => {
        const kpiObj = kpi.data;
        const keys = Object.keys(kpiObj);
        keys?.forEach(key => {
            const newObj = kpiObj[key];
            if (!isOperationalParametersEnergyCapabilityEnabled) {
                operationalSignals.push(newObj);
                return;
            }
            // Filter for operational parameters based on timeseriesCategory field from API
            // Fallback: if no timeseriesCategory or if it's not Energy, consider it operational
            const isOperationalByCategory = newObj.timeseriesCategory === TIME_SERIES_CATEGORY.OPERATIONAL;
            if (isOperationalByCategory) {
                operationalSignals.push(newObj);
            }
        });
    });
    return operationalSignals;
});

const getSelectedTrendKPIs = ({ operationalParameters }) => {
    return operationalParameters?.preselectedKPIs?.single?.trend;
};

// Get count of selected energy signals
const getSelectedEnergySignalsCount = createSelector([
    getSelectedTrendKPIs,
    getEnergyRelatedKPIsList
], (selectedKPIs, energyKPIs) => {
    if (!selectedKPIs?.length) {
        return 0;
    }

    const energyKPIKeys = new Set(energyKPIs.map(kpi => kpi.timeseriesKey));
    return selectedKPIs.filter(selectedKPI => energyKPIKeys.has(selectedKPI)).length;
});

// Get count of selected operational parameters (non-energy signals)
const getSelectedOperationalParametersCount = createSelector([
    getSelectedTrendKPIs,
    getEnergyRelatedKPIsList
], (selectedKPIs, energyKPIs) => {
    if (!selectedKPIs?.length) {
        return 0;
    }

    const energyKPIKeys = new Set(energyKPIs.map(kpi => kpi.timeseriesKey));
    return selectedKPIs.filter(selectedKPI => !energyKPIKeys.has(selectedKPI)).length;
});

export const getInternalActiveTab = ({ operationalParameters }) => {
    return operationalParameters?.internalActiveTab;
};

export const getModel = createSelector([
    getSelectedAsset,
    getSelectedAssetV2,
    getIsInternalTabLimitActive,
    getAssetActiveTab,
    getSelectedEnergySignalsCount,
    getSelectedOperationalParametersCount,
    getEnergyRelatedKPIsList,
    getOperationalParametersKPIsList,
    getInternalActiveTab
], (
    asset,
    assetV2,
    isInternalTabLimitActive,
    assetActiveTab,
    selectedEnergySignalsCount,
    selectedOperationalParametersCount,
    energyRelatedKPIsList,
    operationalParametersKPIsList,
    internalActiveTab
) => {
    return {
        asset,
        assetV2,
        isInternalTabLimitActive,
        assetActiveTab,
        selectedEnergySignalsCount,
        selectedOperationalParametersCount,
        energyRelatedKPIsList,
        operationalParametersKPIsList,
        internalActiveTab
    };
});
